/**
 * Auth Context - Global authentication state
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi, User, LoginRequest, clientsApi } from '../api';
import { isAuthenticated, clearTokens } from '../api/client';

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
}

interface LoginResult {
    verificationRequired?: boolean;
    requires2FA?: boolean;
    deviceId?: string;
    userId?: string;
    challengeId?: string; // New: capture challengeId for OTP
    message?: string;
}

interface AuthContextType extends AuthState {
    login: (data: LoginRequest) => Promise<LoginResult>;
    verify2FA: (userId: string, deviceId: string, token: string, latitude?: number, longitude?: number) => Promise<void>;
    verifyDeviceOtp: (challengeId: string, code: string, serviceKey?: string) => Promise<void>;
    logout: () => void;
    refreshProfile: () => Promise<void>;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: isAuthenticated(),
        isLoading: true,
        error: null,
    });

    // Load user profile on mount if authenticated
    useEffect(() => {
        const loadUser = async () => {
            if (isAuthenticated()) {
                try {
                    const authUser = await authApi.getProfile();
                    let fullUser = authUser;

                    // Fetch extended client profile to get 'statut' for Clients/Agents
                    if (authUser.role !== 'Admin') {
                        try {
                            const clientProfile = await clientsApi.getMyProfile();
                            if (clientProfile.success && clientProfile.client) {
                                fullUser = { ...authUser, ...clientProfile.client };
                            }
                        } catch (e) {
                            console.error('Failed to fetch client profile', e);
                        }
                    }

                    setState(prev => ({
                        ...prev,
                        user: fullUser,
                        isAuthenticated: true,
                        isLoading: false,
                    }));
                } catch (error) {
                    // Token invalid - clear everything
                    clearTokens();
                    setState({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null,
                    });
                }
            } else {
                setState(prev => ({ ...prev, isLoading: false }));
            }
        };

        loadUser();
    }, []);

    // Login
    const login = useCallback(async (data: LoginRequest) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));


        try {
            const response = await authApi.login(data);

            // Handle verification required (new device)
            if (response.status === 'verification_required') {
                setState(prev => ({ ...prev, isLoading: false }));
                return {
                    verificationRequired: true,
                    deviceId: response.deviceId,
                    challengeId: response.challengeId || response.challenge_id, // Capture challengeId (camel or snake)
                    message: response.message,
                };
            }

            // Handle 2FA required
            if (response.status === '2fa_required') {
                setState(prev => ({ ...prev, isLoading: false }));
                return {
                    requires2FA: true,
                    deviceId: response.deviceId,
                    userId: response.userId,
                };
            }

            // Successful login with tokens
            if (response.user) {
                let fullUser = response.user;

                // Fetch extended client profile to get 'statut' for Clients/Agents
                if (response.user.role !== 'Admin') {
                    try {
                        const clientProfile = await clientsApi.getMyProfile();
                        if (clientProfile.success && clientProfile.client) {
                            fullUser = { ...response.user, ...clientProfile.client };
                        }
                    } catch (e) {
                        console.error('Failed to fetch client profile after login', e);
                    }
                }

                setState({
                    user: fullUser,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            }

            return {};
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Erè koneksyon';
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));
            throw error;
        }
    }, []);

    // Verify 2FA
    const verify2FA = useCallback(async (userId: string, deviceId: string, token: string, latitude?: number, longitude?: number) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await authApi.verify2FALogin(userId, deviceId, token, latitude, longitude);
            if (response.user) {
                setState({
                    user: response.user,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Kòd 2FA pa bon';
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));
            throw error;
        }
    }, []);

    // Verify Device OTP (New device flow)
    const verifyDeviceOtp = useCallback(async (challengeId: string, code: string, serviceKey?: string) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await authApi.verifyDeviceOtp(challengeId, code, serviceKey);
            if (response.user) {
                setState({
                    user: response.user,
                    isAuthenticated: true,
                    isLoading: false,
                    error: null,
                });
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Kòd sekirite a pa bon';
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));
            throw error;
        }
    }, []);


    // Logout - clears auth and redirects to login (no back navigation)
    const logout = useCallback(() => {
        authApi.logout();
        setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
        });
        // Use replace to prevent back navigation to protected pages
        window.location.replace('/login');
    }, []);

    // Refresh profile
    const refreshProfile = useCallback(async () => {
        try {
            const user = await authApi.getProfile();
            setState(prev => ({ ...prev, user }));
        } catch (error) {
            console.error('Failed to refresh profile', error);
        }
    }, []);

    // Clear error
    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                verify2FA,
                verifyDeviceOtp,
                logout,
                refreshProfile,
                clearError,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
